import API_BASE_URL from '../config/api';

/**
 * Service API pour la gestion des filtres
 */

/**
 * Récupère toutes les options disponibles pour les filtres
 * @returns {Promise<Object>} Options de filtres (villes, types de cuisine, etc.)
 */
export const getFilterOptions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/restaurants/filters/options`);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des options de filtres');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur getFilterOptions:', error);
    throw error;
  }
};

/**
 * Récupère les restaurants avec filtres
 * @param {Object} filters - Filtres à appliquer
 * @param {number} skip - Nombre d'éléments à sauter (pagination)
 * @param {number} limit - Nombre d'éléments à récupérer
 * @param {string} sortBy - Champ de tri (rating, name, created_at)
 * @param {string} sortOrder - Ordre de tri (asc, desc)
 * @returns {Promise<Array>} Liste des restaurants
 */
export const getFilteredRestaurants = async ({ 
  filters = {}, 
  skip = 0, 
  limit = 20, 
  sortBy = 'rating', 
  sortOrder = 'desc' 
}) => {
  try {
    const params = new URLSearchParams();
    params.append('skip', skip);
    params.append('limit', limit);
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder);

    // Ajouter les filtres
    if (filters.city && filters.city.length > 0) {
      // Prendre la première ville sélectionnée (l'API ne supporte qu'une ville à la fois)
      params.append('city', filters.city[0]);
    }

    if (filters.cuisine && filters.cuisine.length > 0) {
      // Prendre le premier type de cuisine sélectionné
      params.append('cuisine_type', filters.cuisine[0]);
    }

    if (filters.rating && filters.rating.length > 0) {
      // Prendre la note minimale la plus basse sélectionnée
      const minRating = Math.min(...filters.rating.map(r => parseFloat(r)));
      params.append('min_rating', minRating);
    }

    if (filters.price && filters.price.length > 0) {
      // Convertir les gammes de prix en max_delivery_fee
      if (filters.price.includes('low')) {
        params.append('max_delivery_fee', '0');
      } else if (filters.price.includes('medium')) {
        params.append('max_delivery_fee', '5');
      }
    }

    if (filters.status && filters.status.includes('open')) {
      params.append('is_active', 'true');
    }

    const response = await fetch(`${API_BASE_URL}/restaurants/?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des restaurants');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur getFilteredRestaurants:', error);
    throw error;
  }
};

/**
 * Récupère les plats avec filtres
 * @param {Object} filters - Filtres à appliquer
 * @param {number} skip - Nombre d'éléments à sauter (pagination)
 * @param {number} limit - Nombre d'éléments à récupérer
 * @param {string} sortBy - Champ de tri (name, price, created_at)
 * @param {string} sortOrder - Ordre de tri (asc, desc)
 * @returns {Promise<Array>} Liste des plats
 */
export const getFilteredDishes = async ({ 
  filters = {}, 
  skip = 0, 
  limit = 20, 
  sortBy = 'name', 
  sortOrder = 'asc',
  categoryId = null
}) => {
  try {
    const params = new URLSearchParams();
    params.append('skip', skip);
    params.append('limit', limit);
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder);

    // Ajouter les filtres
    if (categoryId) {
      params.append('category_id', categoryId);
    }

    // Gérer le filtre de prix (range slider)
    if (filters.priceRange && filters.priceRange.min !== undefined && filters.priceRange.max !== undefined) {
      params.append('min_price', filters.priceRange.min);
      params.append('max_price', filters.priceRange.max);
    }

    if (filters.diet && filters.diet.length > 0) {
      // Pour l'instant, l'API ne supporte qu'un seul diet_type à la fois
      params.append('diet_type', filters.diet[0]);
    }

    if (filters.cuisine && filters.cuisine.length > 0) {
      params.append('cuisine_type', filters.cuisine[0]);
    }

    if (filters.delivery && filters.delivery.length > 0) {
      // Pas encore implémenté dans l'API backend pour les plats
      // On pourrait filtrer par restaurant.delivery_fee
    }

    if (filters.rating && filters.rating.length > 0) {
      // Note: L'API backend n'a pas encore de filtre par note pour les plats
      // Il faudrait ajouter cela au backend
    }

    const response = await fetch(`${API_BASE_URL}/dishes/?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des plats');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur getFilteredDishes:', error);
    throw error;
  }
};

/**
 * Recherche de plats par terme
 * @param {string} query - Terme de recherche
 * @param {Object} filters - Filtres additionnels
 * @param {number} skip - Pagination
 * @param {number} limit - Nombre de résultats
 * @returns {Promise<Array>} Liste des plats trouvés
 */
export const searchDishes = async ({ 
  query, 
  filters = {}, 
  skip = 0, 
  limit = 20 
}) => {
  try {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('skip', skip);
    params.append('limit', limit);

    if (filters.category) {
      params.append('category', filters.category);
    }

    if (filters.city && filters.city.length > 0) {
      params.append('city', filters.city[0]);
    }

    const response = await fetch(`${API_BASE_URL}/dishes/search?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Erreur lors de la recherche de plats');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur searchDishes:', error);
    throw error;
  }
};

/**
 * Recherche de restaurants par terme
 * @param {string} query - Terme de recherche
 * @param {Object} filters - Filtres additionnels
 * @param {number} skip - Pagination
 * @param {number} limit - Nombre de résultats
 * @returns {Promise<Array>} Liste des restaurants trouvés
 */
export const searchRestaurants = async ({ 
  query, 
  filters = {}, 
  skip = 0, 
  limit = 20 
}) => {
  try {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('skip', skip);
    params.append('limit', limit);

    if (filters.city && filters.city.length > 0) {
      params.append('city', filters.city[0]);
    }

    const response = await fetch(`${API_BASE_URL}/restaurants/search?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Erreur lors de la recherche de restaurants');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur searchRestaurants:', error);
    throw error;
  }
};

export default {
  getFilterOptions,
  getFilteredRestaurants,
  getFilteredDishes,
  searchDishes,
  searchRestaurants
};
